import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, FileText, Gauge, Stamp } from "lucide-react";
import { PageHeader } from "./fordon";
import { HubEditorial, faqSchema } from "@/components/HubEditorial";
import { FiNav, FiSwedishLink } from "@/components/FiNav";
import { FiTuontiCalculator } from "@/components/FiTuontiCalculator";
import { fiTuontiFaq, fiTuontiSections } from "@/data/fi-tuonti";

const TITLE = "Asuntoauton tuonti Ruotsista Suomeen – autovero ja laskuri";
const DESC =
  "Näin tuot asuntoauton tai asuntovaunun Ruotsista Suomeen: autovero, siirtolupa, rekisteröintikatsastus ja kokonaiskustannus. Laskuri + apu suomeksi Kalixista.";
const URL = "https://www.akhusbilfritid.se/fi/tuonti";

const STEPS = [
  { icon: <FileText size={18} />, t: "Paperit meiltä", d: "Kauppakirja, rekisteriote, kosteusmittaus ja kaasutarkastus." },
  { icon: <Gauge size={18} />, t: "Autovero", d: "Ilmoitus Verohallinnolle ennen rekisteröintiä." },
  { icon: <Stamp size={18} />, t: "Katsastus", d: "Rekisteröintikatsastus ja suomalaiset kilvet." },
];

export const Route = createFileRoute("/fi/tuonti")({
  // FAQ-schemat byggs i loadern så guidens textdata hamnar i sidans egen
  // chunk i stället för i initial-bundlen.
  codeSplitGroupings: [["loader", "component"]],
  loader: () => ({ faqLd: faqSchema(fiTuontiFaq) }),
  head: ({ loaderData }) => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "article" },
      { property: "og:locale", content: "fi_FI" },
      { property: "og:image", content: "https://www.akhusbilfritid.se/og-image.jpg" },
      { property: "og:image:alt", content: "Asuntoauton tuonti Ruotsista Suomeen – AK Husbil & Fritid, Kalix" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://www.akhusbilfritid.se/og-image.jpg" },
    ],
    links: [{ rel: "canonical", href: URL }, { rel: "alternate", hrefLang: "fi-FI", href: URL }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(fiTuontiFaq)) }],
  }),
  component: FiTuontiPage,
});

function FiTuontiPage() {
  return (
    <>
      <PageHeader
        label="Tuontiopas · Palvelemme suomeksi"
        title={
          <>
            Tuonti Ruotsista <span className="font-italic-display text-accent">Suomeen</span>.
          </>
        }
        sub="Autovero, siirtolupa, rekisteröintikatsastus ja kokonaiskustannus — koko prosessi järjestyksessä ja ilman kaunistelua. Laske oma arviosi laskurilla ja soita, niin käydään laskelma läpi suomeksi."
      />

      <FiNav />

      <section className="container-edge py-10">
        <div className="grid gap-4 sm:grid-cols-3">
          {STEPS.map((s) => (
            <div key={s.t} className="rounded-3xl border border-border bg-card p-6">
              <span className="grid h-10 w-10 place-items-center rounded-full bg-accent/15 text-accent">{s.icon}</span>
              <h2 className="mt-4 font-display text-xl">{s.t}</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      <FiTuontiCalculator />

      <HubEditorial
        eyebrow="Tuontiopas"
        sections={fiTuontiSections}
        faq={fiTuontiFaq}
        faqTitle="Usein kysytyt kysymykset"
      />

      <section className="container-edge pb-14">
        <div className="rounded-3xl border border-border bg-card p-6 md:p-8">
          <p className="eyebrow">Seuraava askel</p>
          <h2 className="mt-3 font-display text-2xl">Katso varasto ja soita</h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            Kerro mihin käyttöön ajoneuvo tulee ja mikä ajokorttiluokka sinulla on, niin ehdotamme vaihtoehdot
            jotka toimivat myös autoveron ja kokonaiskustannuksen kannalta.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to="/fi/asuntoautot"
              className="inline-flex items-center gap-2 btn-shine rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
            >
              Asuntoautot <ArrowRight size={14} />
            </Link>
            <Link
              to="/fi/asuntovaunut"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary"
            >
              Asuntovaunut <ArrowRight size={14} />
            </Link>
            <Link
              to="/fi/yhteystiedot"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary"
            >
              Yhteystiedot <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <FiSwedishLink to="/guider" label="Guider på svenska" />
    </>
  );
}
